import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { mediaUrl } from '../api/client';
import Seo from '../components/Seo';
import Breadcrumb from '../components/common/Breadcrumb';
import { PageLoader } from '../components/common/Spinner';
import { AccordionItem } from '../components/common/Accordion';
import { SocialLinks } from '../components/layout/Footer';
import { useSettings } from '../context/SettingsContext';
import { useUI } from '../context/UIContext';
import { stripHtml } from '../utils/format';
import NotFound from './NotFound';

function ContactForm() {
  const { toast } = useUI();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/contact', form);
      toast(r.message);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <form onSubmit={submit} className="sf__contact-form">
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <input className="form-control w-full align-middle" placeholder="Name" required {...f('name')} />
        <input className="form-control w-full align-middle" type="email" placeholder="Email" required {...f('email')} />
      </div>
      <input className="form-control w-full align-middle mb-4" placeholder="Phone (optional)" {...f('phone')} />
      <textarea className="form-control w-full align-middle mb-4" rows={6} placeholder="Message" required {...f('message')} />
      <button type="submit" className="sf__btn sf__btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Submit Now'}</button>
    </form>
  );
}

export default function Page() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setLoading(true);
    setMissing(false);
    api.get(`/pages/slug/${slug}`).then((r) => setPage(r.page)).catch(() => setMissing(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PageLoader />;
  if (missing || !page) return <NotFound />;
  const g = settings.general || {};
  const faqLd = page.template === 'faq' && page.faqs?.length
    ? { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: page.faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: stripHtml(f.answer) } })) }
    : null;

  return (
    <>
      <Seo title={page.title} description={page.subtitle || stripHtml(page.content)} seo={page.seo || {}} image={page.banner} jsonLd={faqLd} breadcrumbs={[{ label: 'Home', url: '/' }, { label: page.title }]} />
      {page.showTitle !== false && (
        <div className="page-header py-8 md:py-12 text-center" style={page.banner ? { backgroundImage: `url(${mediaUrl(page.banner)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
          <div className="container-fluid">
            <h1 className="page-header__title text-3xl md:text-4xl">{page.title}</h1>
            {page.subtitle && <p className="text-color-secondary mt-2">{page.subtitle}</p>}
          </div>
        </div>
      )}
      <Breadcrumb items={[{ label: page.title }]} />
      <div className={`section-my ${page.template === 'wide' ? 'container-fluid' : 'container'}`} style={page.template === 'default' ? { maxWidth: 960 } : undefined}>
        {page.template === 'contact' ? (
          <div className="flex flex-wrap -mx-4">
            <div className="w-full lg:w-2/3 px-4 mb-10">
              <h3 className="text-2xl font-medium mb-2">{settings.pages?.contactHeading || 'We would love to hear from you.'}</h3>
              <p className="text-color-secondary mb-6">{settings.pages?.contactText}</p>
              {page.content && <div className="rte mb-6" dangerouslySetInnerHTML={{ __html: page.content }} />}
              <ContactForm />
            </div>
            <div className="w-full lg:w-1/3 px-4">
              <h3 className="font-medium mb-5 text-lg">Information</h3>
              <div className="rte text-color-secondary mb-8">
                {g.address && <p>{g.address}</p>}
                {g.phone && <p><a href={`tel:${g.phone.replace(/[^\d+]/g, '')}`}>{g.phone}</a></p>}
                {g.email && <p><a href={`mailto:${g.email}`}>{g.email}</a></p>}
                {g.whatsapp && <p><a href={`https://wa.me/${g.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">WhatsApp: {g.whatsapp}</a></p>}
              </div>
              <h3 className="font-medium mb-5 text-lg">Social Media</h3>
              <div className="-mx-4 mb-8"><SocialLinks /></div>
              <h3 className="font-medium mb-5 text-lg">We're Open</h3>
              <div className="rte text-color-secondary"><p>Our store has re-opened for shopping, exchanges</p><p>{g.openingHours || 'Every day 11am to 7pm'}</p></div>
            </div>
          </div>
        ) : page.template === 'faq' ? (
          <div className="sf__faq">
            {page.content && <div className="rte mb-8" dangerouslySetInnerHTML={{ __html: page.content }} />}
            {(page.faqs || []).map((f, i) => (
              <AccordionItem key={i} title={f.question} open={i === 0}>
                <div className="rte text-color-secondary" dangerouslySetInnerHTML={{ __html: f.answer }} />
              </AccordionItem>
            ))}
          </div>
        ) : (
          <div className="rte prose eg-page-content" dangerouslySetInnerHTML={{ __html: page.content }} />
        )}
      </div>
    </>
  );
}
